/** @format */
import Script from 'next/script';
import React, {
    FunctionComponent,
    useContext,
    useEffect,
    useMemo,
} from 'react';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import { logger } from '@toucan/client/global/application';
import { MarketContext } from '@toucan/client/global/market';

import { useAutoLoadIntercom } from './hooks/use-auto-load-intercom';
import { useIdentityVerification } from './hooks/use-identity-verification';
import { IntercomScriptProps } from './types';

export const IntercomScript: FunctionComponent<IntercomScriptProps> = ({
    enabled: manualEnabled = false,
    verticalPadding = 20,
    onLoadCommand = 'show',
}) => {
    const { activeMarket } = useContext(MarketContext);

    const { publicRuntimeConfig } = getConfig();

    const workspaceId =
        publicRuntimeConfig?.intercom?.[activeMarket.code]?.workspaceId;

    // We lazily load the Intercom script on first activation unless we are
    // on specific pages
    const shouldAutoLoadScript = useAutoLoadIntercom();

    const shouldLoadScript = manualEnabled || shouldAutoLoadScript;

    const identityVerification = useIdentityVerification({
        enabled: shouldLoadScript,
        includeContactInformation: manualEnabled,
    });

    if (activeMarket.code && !workspaceId) {
        logger.error('Intercom workspaceId not configured for market');
        logger.addLabels({ mpb_market: activeMarket.code });
    }

    const intercomScriptSrc = workspaceId
        ? `https://widget.intercom.io/widget/${workspaceId}`
        : undefined;

    const intercomSettings = useMemo(() => {
        const identity = identityVerification.isSuccess
            ? {
                  user_id: identityVerification.data.user_id,
                  user_hash: identityVerification.data.user_hash,
                  email: identityVerification.data.email,
                  name: identityVerification.data.name,
              }
            : {};

        return {
            app_id: workspaceId,
            hide_default_launcher: true,
            custom_launcher_selector: '.intercom-trigger',
            language_override: activeMarket.language,
            vertical_padding: verticalPadding,
            ...identity,
        };
    }, [
        workspaceId,
        activeMarket.language,
        verticalPadding,
        identityVerification.isSuccess,
        identityVerification.data?.user_id,
        identityVerification.data?.user_hash,
        identityVerification.data?.email,
        identityVerification.data?.name,
    ]);

    useEffect(() => {
        const win: any = window;
        const intercom = win.Intercom;

        win.intercomSettings = {
            ...win.intercomSettings,
            ...intercomSettings,
        };

        // If Intercom is active, update settings
        if (typeof intercom === 'function') {
            intercom('reattach_activator');
            intercom('update', win.intercomSettings);
        } else {
            const intercomPlaceholder: any = function () {
                // eslint-disable-next-line prefer-rest-params
                intercomPlaceholder.c(arguments);
            };

            intercomPlaceholder.q = [] as any[];
            intercomPlaceholder.c = function (args: any) {
                intercomPlaceholder.q.push(args);
            };
            win.Intercom = intercomPlaceholder;
        }
    }, [intercomSettings]);

    if (!shouldLoadScript) {
        return null;
    }

    return (
        <Script
            id="next-script-intercom"
            data-testid="intercom-script"
            strategy="afterInteractive"
            src={intercomScriptSrc}
            onLoad={() => {
                if (manualEnabled) {
                    window?.Intercom?.(onLoadCommand);
                }
            }}
        />
    );
};
